import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChildren,
  QueryList,
  HostListener,
  inject,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { NiIconComponent } from '../ni-icon/ni-icon.component';

export interface StepperStep {
  title: string;
  status: 'completed' | 'inProgress' | 'pending' | 'rejected';
  route?: string;
  icon?: string;
  index?: number;
  sectionId?: string;
  statusLabel?: string;
  statusLabelClass?: string;
}

interface StatusColors {
  bg: string;
  border: string;
  text: string;
  line: string;
}
@Component({
  selector: 'NiFormProgressStepper',
  templateUrl: './ni-progress-stepper.component.html',
  standalone: true,
  imports: [CommonModule, NiIconComponent],
})
export class NiProgressStepperComponent {
  @Input() stepperData: StepperStep[] = [];
  @Input() activeSectionId: string | null = null;
  @Input() disableNavigation: boolean = false;
  @Output() onNavigate = new EventEmitter<StepperStep>();
  @Output() onBackClick = new EventEmitter<void>();

  private readonly location = inject(Location);

  @ViewChildren('stepRef') stepRefs!: QueryList<ElementRef<HTMLDivElement>>;

  lineHeights: number[] = [];

  private resizeObserver!: ResizeObserver;

  statusColors: Record<StepperStep['status'], StatusColors> = {
    completed: {
      bg: 'bg-success-500',
      border: 'border-success-500',
      text: 'text-white',
      line: 'bg-gray-600',
    },
    inProgress: {
      bg: 'bg-primary-500',
      border: 'border-primary-500',
      text: 'text-white',
      line: 'bg-gray-600',
    },
    pending: {
      bg: 'bg-gray-600',
      border: 'border-gray-600',
      text: 'text-secondary-500',
      line: 'bg-gray-600',
    },
    rejected: {
      bg: 'bg-danger-500',
      border: 'border-danger-500',
      text: 'text-white',
      line: 'bg-gray-600',
    },
  };

  ngAfterViewInit(): void {
    this.calculateLineHeights();
    this.resizeObserver = new ResizeObserver(() => this.calculateLineHeights());
    this.stepRefs.forEach((ref) => this.resizeObserver.observe(ref.nativeElement));
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  calculateLineHeights(): void {
    const refs = this.stepRefs.toArray().map((ref) => ref.nativeElement);
    if (refs.length < 2) return;

    const heights: number[] = [];
    for (let i = 0; i < refs.length - 1; i++) {
      const current = refs[i];
      const next = refs[i + 1];
      const h = next.offsetTop - current.offsetTop - current.offsetHeight;
      heights.push(h);
    }
    this.lineHeights = heights;
  }

  handleNavigation(step: StepperStep, index: number): void {
    if (!this.disableNavigation) {
      this.onNavigate.emit({ ...step, index });
      this.showMobileSteps = false
    }
  }

  getStatusColors(status: StepperStep['status']): StatusColors {
    return this.statusColors[status];
  }

  isMobile = false;
  showMobileSteps = false;
  ngOnInit() {
    this.updateScreenSize();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateScreenSize();
  }

  updateScreenSize() {
    this.isMobile = window.innerWidth < 600;
  }

  checkMobile() {
    this.isMobile = window.innerWidth < 640;
  }

  get currentStepIndex(): number {
    if (this.activeSectionId && this.stepperData?.length) {
      const bySection = this.stepperData.findIndex((s) => s.sectionId === this.activeSectionId);
      if (bySection !== -1) {
        return bySection;
      }
    }
    const inProgress = this.stepperData.findIndex((s) => s.status === 'inProgress');
    if (inProgress !== -1) {
      return inProgress;
    }
    return 0;
  }

  get progressPercent(): number {
    return ((this.currentStepIndex + 1) / this.stepperData.length) * 100;
  }

  navigateBack(event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    this.onBackClick.emit();
  }

}
